import { cn } from "@/lib/utils";
import { ChevronLeft } from "lucide-react";
import { ComponentProps, ComponentRef, forwardRef } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Button } from "./ui/button";

const BackTrigger = forwardRef<
  ComponentRef<typeof Button>,
  ComponentProps<typeof Button> & {
    mode?: "strict" | "history" | "segmentCut";
    to?: string;
    segmentCutParts?: number;
    previousPagesCount?: number;
    ignoreState?: boolean;
  }
>(
  (
    {
      className,
      mode = "segmentCut",
      to = "/",
      segmentCutParts = 1,
      previousPagesCount = 1,
      ignoreState = false,
      ...props
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const { pathname, state } = useLocation();

    const handleClick = () => {
      if (mode === "history") {
        return navigate(-previousPagesCount);
      }
    };

    const getPreviousPath = () => {
      if (!ignoreState) {
        const availableNames = ["previousPath", "from", "backPath", "backTo"];
        const previousPath = Object.entries(
          state && typeof state === "object" ? state : {},
        ).find(
          ([key, value]) =>
            availableNames.includes(key) && typeof value === "string",
        )?.[1] as string | undefined;

        if (previousPath) {
          return previousPath;
        }
      }

      if (mode === "segmentCut") {
        const parts = pathname
          .split("/")
          .slice(0, -segmentCutParts)
          .filter(Boolean);
        return `/${parts.join("/")}`;
      }

      return to;
    };

    const content = (
      <>
        <ChevronLeft />
        <span className="sr-only">Go back</span>
      </>
    );

    return (
      <Button
        ref={ref}
        data-sidebar="trigger"
        variant="ghost"
        size="icon"
        className={cn("h-7 w-7", className)}
        onClick={mode === "history" ? handleClick : undefined}
        asChild={mode !== "history"}
        {...props}
      >
        {mode === "history" ? (
          content
        ) : (
          <Link role="button" to={getPreviousPath()} replace>
            {content}
          </Link>
        )}
      </Button>
    );
  },
);
BackTrigger.displayName = "BackTrigger";

export default BackTrigger;
